import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { useGiftGuideHubGate, useGiftGuideHubLink } from './giftGuideHubFlag';
import { CollectionNavigationContext, HubBoardWashContext, HubFrameContext, HubWideGutterContext } from './HubFrameContext';
import { HubLoginButton, HubNavigationView } from './HubNavigationView';
import { resolveHubNavigation } from './hubNavigation';
import { useHubOccasions } from './useHubDocs';
import { useAuthGate } from '../auth/AuthGateContext';
import { useAuth } from '../firebase/FirebaseContext';
import { useAuthSnapshot } from '../hooks/useAuthSnapshot';
import { HubAccountMenu } from './HubAccountMenu';
import { EmailVerificationNotice } from '../auth/EmailVerificationNotice';
import { registerBottomBar } from '../consent/bottomBarInset';
import { useV4AccountState } from '../v4/useV4AccountState';
import { useHubYouTabDot } from './useHubYouTabDot';

const WhitePage = createGlobalStyle`
  html, body { background: white; overscroll-behavior-x: none; overscroll-behavior-y: auto; }
`;
const Content = styled.div`
  --background: 0 0% 100%;
  --hub-page-background: white;
  background: white;
  min-height: 100dvh;
  padding-bottom: 72px;
  /* THE-1187 QA round 2: background-color, not the background shorthand
     — the shorthand resets background-image too, which clobbers
     LikedPage Outer's gradient wash painted on this same child div even
     when the override below sets it back to transparent.

     QA round 3 (shepherd, 2026-10-26): background-color alone no longer
     resets background-image EITHER, which means every OTHER hub page's
     direct child now keeps whatever background-image it painted — e.g.
     AllSavedPage's own Outer gradient (bare /liked, a different page
     from this one) used to be forced to flat white here and would now
     bleed through. Restoring the explicit background-image: none fixed
     that, but round 3 scoped it away from liked-detail with
     :not([data-hub-page="liked-detail"]), which raised this rule's own
     specificity (one class + one attribute selector + one child element)
     past CollectionBanner's && self-reference (two classes) — so this
     base reset started beating the banner's own wash on every collection
     page too (shepherd re-QA round 4, 17% pixel diff vs prod).

     QA round 4 (shepherd, 2026-10-26): back to this rule's ORIGINAL,
     unconditional specificity (one class + one child element) — it
     applies the same flat reset to every page's direct child again,
     liked-detail included. liked-detail is exempted the same way
     CollectionBanner already exempts collection pages from this exact
     rule: LikedPage's own Outer wraps its gradient in && (see
     LikedPage.tsx), which out-specifies this base rule without this rule
     needing to know liked-detail exists. */
  & > div, & > main {
    background-color: white;
    background-image: none;
  }
  &[data-hub-page="calendar"], &[data-hub-page="liked"] { padding-bottom: 0; }
  &[data-hub-page="liked"] > div { box-sizing: border-box; padding-bottom: 72px; }
  @media (min-width: 1024px) {
    &[data-hub-page="liked"] > div { padding-bottom: 0; height: calc(100dvh - 76px); }
  }
  /* THE-1187: the per-person Saved page (/liked/:recipientId) is its OWN
     data-hub-page value, "liked-detail" — not "liked" — because it no
     longer shares "liked"'s (the all-saves page's) contract. That page hides
     the frame's bottom tab bar (see the hideMobileTabBar prop below) and
     scrolls as a normal document (LikedPage's own Outer/Content carry no
     height bound any more), so it needs neither "liked"'s 72px bottom-bar
     clearance nor its desktop fixed-viewport height override just above —
     that fixed height is exactly what produced the inner-scrolling box Kate
     measured (THE-1187): LikedPage's own height: 100dvh fighting this
     frame's calc(100dvh - 76px) override over the same element. Zero
     padding, no height override.

     QA round 4: the top nav no longer renders as a sibling ahead of this
     element either — EnabledFrame hands it to LikedPage via
     CollectionNavigationContext instead (see isLikedDetail above), so
     LikedPage can paint it INSIDE its own wash rather than above it.

     QA (8915d937): the base rules above also force background: white onto
     this element AND onto its direct > div child — LikedPage's own Outer
     draws the person's accentWashStart gradient on exactly that child, so
     the frame's white was painting over it and the mock's wash never
     reached the hub frame. Transparent here lets LikedPage's own background
     show through; nothing else reads background: white off this
     attribute.

     QA round 2: both overrides here were still the background shorthand,
     which resets background-image to none even when the color itself
     is transparent — that is what was erasing LikedPage Outer's gradient
     rather than letting it through. background-color only touches the
     color channel, so the gradient background-image set by LikedPage's
     own Outer survives. */
  &[data-hub-page="liked-detail"] {
    padding-bottom: 0;
    background-color: transparent;
    > div, > main { background-color: transparent; }
  }
  [data-calendar-scroller], [data-hub-scroll-root] {
    overscroll-behavior-x: none;
    overscroll-behavior-y: auto;
  }
  @media (min-width: 1024px) {
    padding-bottom: 0;
    [data-hub-board-layout] { height: calc(100dvh - 76px); }
  }
`;

function EnabledFrame({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const hrefFor = useGiftGuideHubLink();
  const occasions = useHubOccasions(!/^\/(guide|gift-guides)\//.test(location.pathname));
  const { requestSignIn } = useAuthGate();
  const auth = useAuth();
  const { isPermanent } = useAuthSnapshot(auth);
  const user = auth.currentUser;
  // THE-975: her boards, for the tap pop-up's headline + pills. This frame
  // only ever mounts with the Hub gate already 'enabled' (see
  // `HubNavigationFrame` below), so no second gate check is needed here —
  // unlike the shared non-Hub-or-Hub doors (`V4YouDoor`, `V4BoardCaptureHost`).
  const { state: accountState } = useV4AccountState();
  const tapBoards = accountState.boards.length > 0 ? accountState.boards : undefined;
  // THE-1263: the board's own desktop wash-start color while its header
  // card is at rest, set by `BoardLayout` via `HubBoardWashContext` (see
  // that context's own doc comment). `null` on every other route — nothing
  // else ever calls the setter.
  const [boardWash, setBoardWash] = useState<string | null>(null);
  // QA (8915d937): this frame stays mounted across navigation (only
  // `children`/`location` change), but `useBottomBarInset` subscribes with
  // a `useEffect([ref])` — a stable ref object never changes identity, so
  // that effect only ever registered the bar it found on THIS component's
  // first mount. THE-1187's own `hideMobileTabBar` unmounts and remounts the
  // `<Mobile>` nav underneath this same ref on every /liked/:id visit, and
  // the one-shot registration never notices either edge: the inset stuck at
  // whatever it was (often 0) for the rest of the session instead of
  // tracking the bar's real presence. A state-backed callback ref re-fires
  // on every mount/unmount of the actual DOM node, so register/unregister
  // runs exactly when the bar really does.
  const [mobileBarEl, setMobileBarEl] = useState<HTMLElement | null>(null);
  useEffect(() => {
    if (!mobileBarEl) return undefined;
    return registerBottomBar(mobileBarEl);
  }, [mobileBarEl]);
  /* The mobile tab bar's You dot (THE-973). Resolved HERE rather than inside
     `HubNavigationView` so the view stays presentational and Firebase-free —
     its stories mount it without a Firestore — and because this component is
     the one place already inside the `useGiftGuideHubGate() === 'enabled'`
     branch: a hook called here cannot subscribe for a visitor whose switch is
     off, which is the switch-off-parity property the whole PRD turns on. */
  const youDot = useHubYouTabDot();
  // THE-1161's full-width header + edge-to-edge carousels (Kate, 2026-10-28,
  // PR #883: no switch of their own — ride the Gift Guide hub preview gate
  // only). `EnabledFrame` only mounts once `useGiftGuideHubGate()` is
  // `'enabled'` (see `HubNavigationFrame` below), so that gate has already
  // decided this is on; there is nothing left to resolve.
  const wideGutterEnabled = true;
  const navigation = resolveHubNavigation(location.pathname, location.state, occasions, location.search);
  const isCollection = occasions.some(occasion => location.pathname.replace(/\/+$/, '') === `/gift-guides/${occasion.key}`);
  // THE-1187 — the SENDER's own per-person Saved page loses the frame's fixed
  // bottom tab bar on phones (Kate 2026-10-24: "remove the bottom tab bar...
  // The floating 'Share list' sits where the bar was"). The recipient's own
  // view of that board, `/b/:shareId`, is a DIFFERENT route and keeps the bar
  // — this only matches the `/liked/:id` drill-down, never bare `/liked`
  // (the all-saves page) or `/b/:shareId`.
  const isLikedDetail = /^\/liked\/[^/]+/.test(location.pathname);
  // QA round 4 (local-board, 2026-10-26): the desktop header rendered as a
  // sibling AHEAD of `Content` in the return below, so even with its own
  // background made transparent there was nothing underneath for it to show
  // through — LikedPage's wash only started once Content's box began, below
  // the header's 76px. `embedded` here (reused from the collection-page case)
  // makes the header's own background transparent; pairing it with the
  // `CollectionNavigationContext` hand-off below (so LikedPage renders this
  // nav INSIDE its own wash, the same way `CollectionView` renders it inside
  // `CollectionBanner`) is what actually puts the wash behind it.
  // THE-1391: the People page (`/`) renders ComingUpStrip directly under this
  // bar, and that strip reclaims ~96px of invisible confetti headroom above
  // its scroll track (ComingUpStrip.tsx) that reaches up into the bar's own
  // box. With the bar `position: static` here (the pre-existing default),
  // nothing stops the browser's hit-test from preferring that later,
  // overlapping box over the header. `pinDesktopBar` fixes the identical
  // overlap on board/quiz-results by lifting the bar `position: sticky` —
  // but `/` is also the first-time marketing homepage, and sticky would
  // start pinning this bar to the viewport while scrolling, which it does
  // not do today. Kate's call (2026-11-03): keep `/` scrolling the bar away
  // byte-for-byte, fix only the clicks. `raiseDesktopBar` opens the same
  // stacking context (`position: relative` + the same z-index `pinDesktopBar`
  // uses) without `top: 0`, so the bar stays in normal flow and still
  // scrolls off with the rest of the document — see `Desktop`'s own doc
  // comment in HubNavigationView.tsx. Root-caused here instead of inside
  // ComingUpStrip so phones (the bar's own mobile sibling never reads either
  // prop) keep today's behaviour byte-for-byte.
  const pinDesktopBar = /^\/(quiz\/results|board)\//.test(location.pathname);
  const raiseDesktopBar = location.pathname === '/';
  const navigationView = <HubNavigationView embedded={isCollection || isLikedDetail} boardWash={boardWash} pinDesktopBar={pinDesktopBar} raiseDesktopBar={raiseDesktopBar} navigation={navigation} pathname={location.pathname} hrefFor={hrefFor}
      mobileRef={setMobileBarEl}
      hideMobileTabBar={isLikedDetail}
      youDot={youDot}
      wideGutterEnabled={wideGutterEnabled}
      /* The Hub's own desktop avatar menu (THE-967 PR 1), NOT
         `HeaderAccountMenu`. The swap is the gate: this frame only renders once
         `useGiftGuideHubGate()` is `enabled`, so with the switch off or pending
         every non-Hub mount point of `HeaderAccountMenu` (SiteHeader,
         PublicBoardAccountSlot, BoardHeaderActions, RecommendationResultsPage's
         flag-off path, the marketing pages) is untouched — that file is not
         edited at all. `confirmBox` is PR 2's real
         `<EmailVerificationNotice user={user} hub />` (SettingsPage.tsx already
         uses it) — without it `HubAccountMenu` falls back to its placeholder
         copy, which is the gap this closes. */
      account={isPermanent ? <HubAccountMenu confirmBox={<EmailVerificationNotice user={user} hub />} /> : <HubLoginButton data-signin-entry="hub-frame-header" onClick={() => requestSignIn({ mode: 'signin', entry: 'hub-frame-header', boards: tapBoards })}>Log in</HubLoginButton>}
      /* Signed in on a phone, account is the You tab's job (today's rule, and
         the reason SiteHeader hides its actions below 1024px) — so the pinned
         mobile bar carries the wordmark alone rather than a second account
         menu. Signed out it carries `Log in`, per canvas p01. */
      mobileAccount={isPermanent ? null : <HubLoginButton data-signin-entry="hub-mobile-header" onClick={() => requestSignIn({ mode: 'signin', entry: 'hub-mobile-header', boards: tapBoards })}>Log in</HubLoginButton>} />;
  return <HubFrameContext.Provider value>
    <HubWideGutterContext.Provider value={wideGutterEnabled}>
    <WhitePage />
    {!isCollection && !isLikedDetail && navigationView}
    <CollectionNavigationContext.Provider value={(isCollection || isLikedDetail) ? navigationView : null}>
      <HubBoardWashContext.Provider value={setBoardWash}>
        <Content data-hub-page={isLikedDetail ? 'liked-detail' : location.pathname.split('/')[1]}>{children}</Content>
      </HubBoardWashContext.Provider>
    </CollectionNavigationContext.Provider>
    </HubWideGutterContext.Provider>
  </HubFrameContext.Provider>;
}

/** pending is today's app. Mounting this frame never redirects, pushes or
 * replaces a location, including when ancestry arrives after a deep link. */
export function HubNavigationFrame({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const supported = pathname === '/' || /^\/(gift-guides|guide|calendar|holidays|settings|you|liked|board|b)(\/|$)/.test(pathname) || pathname.startsWith('/quiz/results/');
  const gate = useGiftGuideHubGate({ skip: !supported });
  return supported && gate === 'enabled' ? <EnabledFrame>{children}</EnabledFrame> : <>{children}</>;
}
